import { isTimeoutApiError } from '@/api/axiosInstance'
import { actionKeys, deviceKeys, deviceSchemaKeys, sessionRuleKeys } from '@/lib/queryKeys'
import { QueryClient } from '@tanstack/react-query'

/** Exported so tests share these settings. A bare QueryClient falls back to TanStack's own
 *  defaults, where staleTime is 0. */
export const queryClientDefaults = {
	queries: {
		staleTime: 1000 * 60 * 5, // 5 minutes
		gcTime: 1000 * 60 * 10, // 10 minutes
		refetchOnWindowFocus: true,
		retry: (failureCount: number, error: unknown) => {
			const status = (error as { status?: number })?.status
			// A rejected request is rejected the same way every time, so retrying only multiplies it.
			if (status === 400 || status === 401 || status === 403) {
				return false
			}
			// Timeouts normalize to status 500, so this must precede the count check.
			if (isTimeoutApiError(error)) {
				return failureCount < 1
			}
			return failureCount < 3
		},
	},
	mutations: {
		retry: false,
	},
}

export const queryClient = new QueryClient({ defaultOptions: queryClientDefaults })

/**
 * Attach/detach/reprovision move devices between projects. Cached device suggestion queries
 * (picker dropdowns, search-bar value suggestions) go stale the moment such an action succeeds —
 * call this so open dropdowns refetch immediately and closed ones refetch on next mount.
 */
export const invalidateDeviceSuggestions = (): Promise<void> =>
	queryClient.invalidateQueries({ queryKey: deviceKeys.allFilterOptions() })

/**
 * The attach form excludes UIDs and target serials that already have a pending attach. Queueing
 * more adds to that set, so drop it after a successful trigger or a reopened form would offer the
 * devices it just claimed.
 */
export const invalidateInflightAttaches = (organizationId: string): Promise<void> =>
	queryClient.invalidateQueries({ queryKey: actionKeys.inflightAttaches(organizationId) })

/**
 * Sessions panels filter by rule name off a cached rule list. Without this, creating or renaming a
 * rule leaves them querying the old names until the entry goes stale on its own.
 */
export const invalidateSessionRules = (): Promise<void> =>
	queryClient.invalidateQueries({ queryKey: sessionRuleKeys.all })

/**
 * The device_state schema is cached per project for five minutes, so creating, renaming or deleting a
 * column on the Streams page would otherwise leave every filter bar and column picker offering the old
 * set until the entry aged out. Called from the api layer rather than the six mutation call sites, so a
 * seventh cannot forget it.
 */
export const invalidateDeviceStateSchema = (): Promise<void> =>
	queryClient.invalidateQueries({ queryKey: deviceSchemaKeys.allStateKeys() })

/**
 * Device filter bars cache the schemas of the streams their state keys point at, to pick each column's
 * operators. A field created, retyped or deleted on the Streams page would otherwise offer the old ones
 * until the entry aged out.
 */
export const invalidateStreamSchemas = (): Promise<void> =>
	queryClient.invalidateQueries({ queryKey: deviceSchemaKeys.allStateKeyStreams() })
